import type { CarType } from "../../../shared-logic/types/CarType";
import type { ItemsType } from "../../../../common-deprecated/redux/typeHelpers";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import type { QueryType } from "../../../../common-deprecated/server/types";
import { find } from "../../../../common-deprecated/utils";
import type {
    ExtraType,
    FinanceOptionType,
    PackType,
    ServiceProductType,
} from "../../../../common-deprecated/types/CommonTypes";
import { getFinancePackContent } from "../../../shared-logic/utils/packUtils";
import { maximizeBasket } from "../../../shared-logic/utils/basketUtils";
import { setTradeInAemIncompatible } from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { isAllowedFinanceProductForTradeIn } from "./settings/bnbSettingUtils";
import type { TradeInAemExternalDataType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import type { BuildAndBuyDispatchType } from "../redux/store";
import type { FinancePackConfigurablesType } from "../../../shared-logic/types/FinanceTypes";

export const maximizeBuildBasket = (
    initCar: CarType,
    items: ItemsType,
    basket: BasketType,
    query: QueryType,
): BasketType => {
    // Get the base object of a basket.
    const baseBasket = maximizeBasket(initCar, items, basket, query);

    // If the car has been loaded by a tyCode loadedCar should be set.
    const car = initCar.tyCodeCar || initCar;

    // Finance packs logic
    const financePacks = (
        query.fp
            ? query.fp.map((id) => find(parseInt(id), items.financePacks))
            : car.financePacks.map((financePack) => find(financePack.id, items.financePacks))
    ).filter(Boolean) as PackType[];

    const financePackExtras = (
        query.fpe
            ? query.fpe.reduce(
                  (prevFel, id) => {
                      const financePackExtra = items.financePacks.reduce(
                          (prevExtra, financePack) => {
                              const foundExtra = find(
                                  parseInt(id),
                                  getFinancePackContent(financePack, false, "monthly", items),
                              );
                              return [...prevExtra, foundExtra];
                          },
                          [] as (FinancePackConfigurablesType | undefined)[],
                      );

                      return [...prevFel, ...financePackExtra];
                  },
                  [] as (FinancePackConfigurablesType | undefined)[],
              )
            : car.extras.reduce(
                  (previousExtras, extra) => {
                      const financePackExtra = items.financePacks.reduce(
                          (prevExtra, financePack) => {
                              const foundExtra = find(
                                  extra.id,
                                  getFinancePackContent(financePack, false, "monthly", items),
                              );

                              return [...prevExtra, foundExtra];
                          },
                          [] as (FinancePackConfigurablesType | undefined)[],
                      );

                      return [...previousExtras, ...financePackExtra];
                  },
                  [] as (FinancePackConfigurablesType | undefined)[],
              )
    ).filter(Boolean) as ExtraType[];

    const financePackServiceProducts = (
        query.fpsp
            ? query.fpsp.reduce(
                  (previousServiceProduct, id) => {
                      const financePackServiceProduct = items.financePacks.reduce(
                          (prevServiceProduct, financePack) => {
                              const foundServiceProduct = find(
                                  parseInt(id),
                                  getFinancePackContent(financePack, false, "monthly", items),
                              );
                              return [...prevServiceProduct, foundServiceProduct];
                          },
                          [] as (FinancePackConfigurablesType | undefined)[],
                      );

                      return [...previousServiceProduct, ...financePackServiceProduct];
                  },
                  [] as (FinancePackConfigurablesType | undefined)[],
              )
            : car.serviceProducts.reduce(
                  (previousServiceProducts, serviceProduct) => {
                      const financePackServiceProduct = items.financePacks.reduce(
                          (prevServiceProduct, financePack) => {
                              const foundServiceProduct = find(
                                  serviceProduct.id,
                                  getFinancePackContent(financePack, false, "monthly", items),
                              );

                              return [...prevServiceProduct, foundServiceProduct];
                          },
                          [] as (FinancePackConfigurablesType | undefined)[],
                      );

                      return [...previousServiceProducts, ...financePackServiceProduct];
                  },
                  [] as (FinancePackConfigurablesType | undefined)[],
              )
    ).filter(Boolean) as ServiceProductType[];

    return {
        ...baseBasket,
        financePacks,
        financePackExtras,
        financePackServiceProducts,
    };
};

/**
 * Checks whether the current trade-in configuration is compatible with the selected finance option.
 * Note that this also gets set in the trade-in reducer.
 */
export const checkAndSetFinanceProductTradeInCompatibility = (
    tradeInData: TradeInAemExternalDataType,
    previousIncompatibleValue: boolean,
    financeOption: FinanceOptionType,
    financeProductName: string | undefined,
    bnbSettings: BnbSettingsType,
    dispatch: BuildAndBuyDispatchType,
): void => {
    const incompatible =
        !!tradeInData.vehicleValue &&
        financeOption === "monthly" &&
        !!financeProductName &&
        !isAllowedFinanceProductForTradeIn(bnbSettings?.disableTradeInForFinanceProducts, financeProductName);

    if (previousIncompatibleValue !== incompatible) dispatch(setTradeInAemIncompatible(incompatible));
};
